import React, {MouseEvent, type ReactElement, type ReactNode} from "react";
import {Link} from "gatsby";

import {
  css,
  isInternalURL,
  generateURLWithTrailingSlash,
} from "/src/utils/utils";

import * as styles from "./link-tag.module.scss";

const ONLY_DATA_PAGES = process.env.ONLY_DATA_PAGES;

interface LinkTagProps {
  children: ReactElement<"span"> | string | ReactNode;
  href: string;
  classes?: string;
  openInNewTab?: boolean;
  role?: string;
  tabIndex?: number;
  ariaLabel?: string;
  onClick?: (e: MouseEvent<HTMLElement>) => unknown;
  skipAddingTrailingSlash?: boolean;
  usePrefixPath?: boolean;
}

const LinkTag = (props: LinkTagProps): ReactElement => {
  const {
    ariaLabel,
    children,
    classes = "",
    href,
    onClick,
    openInNewTab = false,
    role,
    tabIndex = 0,
    // TODO: Clean this up later
    skipAddingTrailingSlash,
    usePrefixPath,
  } = props;

  const isInternalLink = isInternalURL(href);

  const finalHref =
    isInternalLink && !skipAddingTrailingSlash
      ? generateURLWithTrailingSlash(href)
      : href;

  // --- Determine if we need to force an anchor tag for root path ---
  const useAnchorForForcedRoot =
    ONLY_DATA_PAGES && isInternalLink && usePrefixPath;

  if ((isInternalLink && !openInNewTab) || useAnchorForForcedRoot) {
    return (
      <Link
        tabIndex={tabIndex}
        to={finalHref}
        className={css(styles.linkTag, classes)}
        onClick={onClick}
        aria-label={ariaLabel}
      >
        {children}
      </Link>
    );
  }

  return (
    <a
      role={role}
      tabIndex={tabIndex}
      className={css(styles.linkTag, classes)}
      href={finalHref}
      onClick={onClick}
      target={openInNewTab ? "_blank" : "_self"}
      rel="noopener noreferrer"
      aria-label={ariaLabel}
    >
      {children}
    </a>
  );
};

export default LinkTag;
